import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import { getUserRoles } from '@/lib/supabase/roles';
import { Loader2 } from '@/components/icons';

type SellerRole = 'admin' | 'partner';

interface SellerRouteProps {
  children: React.ReactNode;
  allowedRoles?: SellerRole[];
}

/**
 * Route protection component for seller/admin features
 * By default allows 'admin' and 'partner' roles
 */
export const SellerRoute: React.FC<SellerRouteProps> = ({ 
  children, 
  allowedRoles = ['admin', 'partner']
}) => {
  const { session } = useSession();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      if (session?.user?.id) {
        const roles = await getUserRoles(session.user.id);
        setHasAccess(roles.some(role => allowedRoles.includes(role as SellerRole)));
      } else {
        setHasAccess(false);
      }
    })();
  }, [session?.user?.id, allowedRoles]);

  // Loading state
  if (hasAccess === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not logged in - redirect to home
  if (!session?.user?.id) {
    return <Navigate to="/" replace />;
  }

  // No access - show access denied
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <span className="text-3xl">🚫</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Acceso Denegado</h1>
          <p className="text-slate-600 mb-6">Solo los vendedores pueden acceder a esta funcionalidad.</p>
          <a 
            href="/" 
            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Volver al Inicio
          </a>
        </div>
      </div>
    );
  }

  // Has access - render children
  return <>{children}</>;
};

// Keep old name for backward compatibility as export alias
export const AdminOnlyRoute = SellerRoute;
